import { useState, useCallback } from 'react';
import { ThumbsUp, ThumbsDown, CheckCircle2 } from 'lucide-react';
import api from '../services/api';
import { FeedbackFailureReason } from '../types';

interface ChatFeedbackProps {
  traceId: string;
}

const FAILURE_REASONS: { value: FeedbackFailureReason; label: string }[] = [
  { value: 'inaccurate', label: 'Inaccurate' },
  { value: 'too_long', label: 'Too long' },
  { value: 'irrelevant', label: 'Irrelevant' },
  { value: 'other', label: 'Other' },
];

/**
 * Inline thumbs-up / thumbs-down feedback widget rendered below each chat answer.
 *
 * UX flow:
 *   Thumbs-up  → immediate POST, show confirmation
 *   Thumbs-down → reveal reason selector → POST on confirm
 *
 * The traceId is forwarded to the backend, which links the score to the
 * exact Langfuse span for triage and eval regression.
 */
export default function ChatFeedback({ traceId }: ChatFeedbackProps) {
  const [rating, setRating] = useState<1 | -1 | null>(null);
  const [failureReason, setFailureReason] = useState<FeedbackFailureReason | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = useCallback(
    async (selectedRating: 1 | -1, reason?: FeedbackFailureReason) => {
      if (submitting || submitted) return;
      setSubmitting(true);
      try {
        await api.feedback.submit({
          traceId,
          rating: selectedRating,
          failureReason: reason,
        });
        setSubmitted(true);
      } catch {
        // Non-fatal — silently swallow; do not interrupt the user workflow
      } finally {
        setSubmitting(false);
      }
    },
    [traceId, submitting, submitted]
  );

  const handleThumbsUp = useCallback(() => {
    setRating(1);
    void submit(1);
  }, [submit]);

  const handleThumbsDown = useCallback(() => {
    setRating(-1);
  }, []);

  const handleReasonConfirm = useCallback(() => {
    if (!failureReason) return;
    void submit(-1, failureReason);
  }, [failureReason, submit]);

  if (submitted) {
    return (
      <div className="flex items-center gap-1.5 text-[11px] text-emerald-700 font-medium">
        <CheckCircle2 className="w-3.5 h-3.5" />
        <span>Feedback recorded — thank you</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-[11px] text-slate-400 font-medium">Was this answer helpful?</p>

      <div className="flex items-center gap-2">
        <button
          id="chat-feedback-thumbs-up"
          onClick={handleThumbsUp}
          disabled={rating !== null || submitting}
          aria-label="Helpful"
          className={[
            'flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-[11px] font-semibold transition',
            'disabled:cursor-not-allowed',
            rating === 1
              ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
              : 'bg-white border-slate-200 text-slate-600 hover:border-emerald-300 hover:text-emerald-700 hover:bg-emerald-50',
          ].join(' ')}
        >
          <ThumbsUp className="w-3.5 h-3.5" />
          Yes
        </button>

        <button
          id="chat-feedback-thumbs-down"
          onClick={handleThumbsDown}
          disabled={rating !== null || submitting}
          aria-label="Not helpful"
          className={[
            'flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-[11px] font-semibold transition',
            'disabled:cursor-not-allowed',
            rating === -1
              ? 'bg-rose-50 border-rose-300 text-rose-800'
              : 'bg-white border-slate-200 text-slate-600 hover:border-rose-300 hover:text-rose-700 hover:bg-rose-50',
          ].join(' ')}
        >
          <ThumbsDown className="w-3.5 h-3.5" />
          No
        </button>
      </div>

      {/* Reason selector — shown only after thumbs-down */}
      {rating === -1 && !submitted && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {FAILURE_REASONS.map(({ value, label }) => (
            <button
              key={value}
              id={`chat-feedback-reason-${value}`}
              onClick={() => setFailureReason(value)}
              className={[
                'px-2.5 py-1 rounded border text-[11px] font-medium transition',
                failureReason === value
                  ? 'bg-rose-100 border-rose-400 text-rose-900'
                  : 'bg-white border-slate-200 text-slate-500 hover:border-slate-400 hover:text-slate-700',
              ].join(' ')}
            >
              {label}
            </button>
          ))}

          <button
            id="chat-feedback-reason-confirm"
            onClick={handleReasonConfirm}
            disabled={!failureReason || submitting}
            className="px-3 py-1 rounded bg-slate-800 text-white text-[11px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-700 transition"
          >
            {submitting ? 'Sending…' : 'Submit'}
          </button>
        </div>
      )}
    </div>
  );
}
